// Story 1.22 — drag-and-drop profile-image upload.
//
// Mirrors Story 1.21's ImageDropzone exactly EXCEPT the mint hook —
// uses useMintProfileImageUploadUrl (POST /profile/image-upload-url
// → blob path starts with `creator-images/profile/{user_id}/`).
// Same client-side validation (PNG/JPG + ≤2MB + ≥512×512px) and the
// same SAS-expired-mid-upload retry-once pattern.

import { useRef, useState } from "react";
import toast from "react-hot-toast";

import { Button } from "@greenhold/components";

import { useMintProfileImageUploadUrl } from "./useMintProfileImageUploadUrl";

const MAX_BYTES = 2 * 1024 * 1024;
const MIN_DIMENSION = 512;

interface Props {
  onUploaded: (blobPath: string, previewUrl: string) => void;
  blobPath: string | null;
  previewUrl: string | null;
}

type UploadState =
  | { kind: "idle" }
  | { kind: "validating" }
  | { kind: "minting" }
  | { kind: "uploading" }
  | { kind: "done" }
  | { kind: "error"; message: string };

export function ProfileImageDropzone({ onUploaded, blobPath, previewUrl }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mintMutation = useMintProfileImageUploadUrl();
  const [state, setState] = useState<UploadState>(
    blobPath ? { kind: "done" } : { kind: "idle" },
  );
  const [dragging, setDragging] = useState(false);

  const validateAndUpload = async (file: File) => {
    setState({ kind: "validating" });

    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      setState({
        kind: "error",
        message: "Only PNG and JPEG images are supported.",
      });
      return;
    }
    if (file.size === 0) {
      setState({ kind: "error", message: "File is empty." });
      return;
    }
    if (file.size > MAX_BYTES) {
      setState({ kind: "error", message: "File is larger than 2 MB." });
      return;
    }

    const dimensionOk = await new Promise<boolean>((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          resolve(img.width >= MIN_DIMENSION && img.height >= MIN_DIMENSION);
        };
        img.onerror = () => resolve(false);
        img.src = e.target?.result as string;
      };
      reader.onerror = () => resolve(false);
      reader.readAsDataURL(file);
    });
    if (!dimensionOk) {
      setState({
        kind: "error",
        message: `Image must be at least ${MIN_DIMENSION}×${MIN_DIMENSION} pixels.`,
      });
      return;
    }

    await mintAndPut(file, false);
  };

  const mintAndPut = async (file: File, isRetry: boolean) => {
    setState({ kind: "minting" });
    let minted;
    try {
      minted = await mintMutation.mutateAsync({
        filename: file.name,
        content_length: file.size,
        content_type: file.type,
      });
    } catch (err) {
      const detail = err as { message?: string };
      setState({
        kind: "error",
        message: detail.message ?? "Failed to mint upload URL.",
      });
      return;
    }

    setState({ kind: "uploading" });
    try {
      const putResp = await fetch(minted.upload_url, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": minted.required_headers["x-ms-blob-type"],
          "x-ms-blob-content-type":
            minted.required_headers["x-ms-blob-content-type"],
        },
        body: file,
      });
      if (!putResp.ok) {
        if (putResp.status === 403 && !isRetry) {
          await mintAndPut(file, true);
          return;
        }
        setState({
          kind: "error",
          message: `Upload failed (HTTP ${putResp.status}). Try again.`,
        });
        return;
      }
    } catch {
      setState({
        kind: "error",
        message: "Upload network error. Check your connection and retry.",
      });
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    onUploaded(minted.blob_path, objectUrl);
    setState({ kind: "done" });
    toast.success("New profile image uploaded");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void validateAndUpload(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void validateAndUpload(file);
    }
  };

  const statusLabel = (() => {
    switch (state.kind) {
      case "validating":
        return "Validating image…";
      case "minting":
        return "Preparing upload…";
      case "uploading":
        return "Uploading…";
      case "done":
        return "Image uploaded ✓";
      case "error":
        return state.message;
      default:
        return null;
    }
  })();
  const isBusy =
    state.kind === "validating" ||
    state.kind === "minting" ||
    state.kind === "uploading";

  return (
    <div className="space-y-2">
      <label
        htmlFor="creator-profile-image-input"
        className="block text-sm font-semibold"
      >
        Profile image
      </label>
      <p className="text-sm opacity-70">
        Replace your current profile image. Square PNG or JPG, at least
        512×512, up to 2 MB.
      </p>
      <div
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        className={[
          "rounded-md border-2 border-dashed p-6 text-center transition-colors",
          dragging
            ? "border-brand-gold-500 bg-brand-paper-200"
            : "border-brand-forest-200 bg-brand-paper-50",
        ].join(" ")}
      >
        {previewUrl ? (
          <div className="flex flex-col items-center gap-3">
            <img
              src={previewUrl}
              alt="Profile image preview"
              className="w-32 h-32 rounded-full object-cover border-4 border-brand-gold-500"
            />
            <Button
              type="button"
              variant="tertiary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
            >
              Replace photo
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <p className="opacity-80">Drag a file here or</p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
            >
              Choose a file
            </Button>
          </div>
        )}
        <input
          ref={fileInputRef}
          id="creator-profile-image-input"
          type="file"
          accept="image/png,image/jpeg"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
      {statusLabel && (
        <p
          className={
            state.kind === "error"
              ? "text-sm text-destructive"
              : "text-sm opacity-70"
          }
        >
          {statusLabel}
        </p>
      )}
    </div>
  );
}
